//TODO: TEMP copy of cwan-ux package component. Found here:
// https://gitlab.com/clearwateranalytics/apps/UIUX/cwan-ux/-/blob/master/packages/cwan-ux-core-react/src/components/TopNavigation.shared.tsx

import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import SettingsIcon from '@mui/icons-material/Settings'
import Box from '@mui/material/Box'
import type { SxProps } from '@mui/material/styles'
import { type Theme } from '@mui/material/styles'
import React, { Children, isValidElement } from 'react'

// Inlined from cwan-ux-core's PaletteGray (Clearwater UX Design System Colors)
// so this local copy has no dependency on that package.
export const PaletteGray = {
  p800: '#52607D',
  p700: '#6C7892',
  p400: '#B8BECF',
}

type TopNavigationSx = SxProps<Theme> | ((theme: Theme) => SxProps<Theme>)

export interface TopNavigationProps {
  children: React.ReactNode
  className?: string
  currentPath?: string
  linkComponent?: React.ElementType
  utilityActions?: readonly TopNavigationUtilityActionProps[]
}

export interface TopNavigationUtilityActionProps {
  ariaLabel: string
  icon: React.ReactNode
  onClick?: () => void
}

export interface TopNavigationBrandProps {
  children: React.ReactNode
  to: string
}

export interface TopNavigationLinkProps {
  children: React.ReactNode
  isActive?: boolean
  rel?: string
  target?: React.HTMLAttributeAnchorTarget
  to: string
}

export interface TopNavigationMenuProps {
  children: React.ReactNode
  disabled?: boolean
  // Shown as a hover tooltip on the menu's top-level button when disabled.
  disabledTooltip?: string
  label: string
}

export interface TopNavigationSectionProps {
  children: React.ReactNode
  label: string
}

export type TopNavigationFooterLinkProps = TopNavigationLinkProps

export interface NavigationLeafSpec {
  isActive: boolean
  key: string
  label: React.ReactNode
  rel?: string
  target?: React.HTMLAttributeAnchorTarget
  to: string
}

export interface NavigationBrandSpec {
  key: string
  label: React.ReactNode
  to: string
}

export interface NavigationSectionSpec {
  items: readonly NavigationLeafSpec[]
  key: string
  label: string
}

export interface NavigationMenuSpec {
  disabled: boolean
  disabledTooltip: string | null
  footerLink: NavigationLeafSpec | null
  isActive: boolean
  key: string
  label: string
  sections: readonly NavigationSectionSpec[]
}

export type NavigationItemSpec =
  | {
      kind: 'link'
      link: NavigationLeafSpec
    }
  | {
      kind: 'menu'
      menu: NavigationMenuSpec
    }

export interface ParsedTopNavigationChildren {
  brand: NavigationBrandSpec | null
  items: readonly NavigationItemSpec[]
}

export interface NavigationLinkRendererProps {
  children: React.ReactNode
  isActive?: boolean
  linkComponent: React.ElementType | undefined
  onClick?: () => void
  rel?: string
  sx: TopNavigationSx
  target?: React.HTMLAttributeAnchorTarget
  to: string
}

interface ChevronProps {
  sx?: TopNavigationSx
}

export const TopNavigationBrandMarker = (props: TopNavigationBrandProps) => {
  void props
  return null
}

export const TopNavigationLinkMarker = (props: TopNavigationLinkProps) => {
  void props
  return null
}

export const TopNavigationMenuMarker = (props: TopNavigationMenuProps) => {
  void props
  return null
}

export const TopNavigationSectionMarker = (
  props: TopNavigationSectionProps,
) => {
  void props
  return null
}

export const TopNavigationFooterLinkMarker = (
  props: TopNavigationFooterLinkProps,
) => {
  void props
  return null
}

const normalizePath = (path: string): string => {
  if (path === '/') {
    return path
  }

  return path.replace(/\/+$/, '')
}

const isCurrentPathMatch = (
  currentPath: string | undefined,
  to: string,
): boolean => {
  if (!currentPath) {
    return false
  }

  const normalizedCurrentPath = normalizePath(currentPath)
  const normalizedTargetPath = normalizePath(to)

  if (normalizedCurrentPath === normalizedTargetPath) {
    return true
  }

  if (normalizedTargetPath === '/') {
    return false
  }

  return normalizedCurrentPath.startsWith(`${normalizedTargetPath}/`)
}

const getElementKey = (element: React.ReactElement, fallback: string): string => {
  if (typeof element.key === 'string' || typeof element.key === 'number') {
    return String(element.key)
  }

  return fallback
}

const isElementOfType = <Props,>(
  child: React.ReactNode,
  component: (props: Props) => null,
): child is React.ReactElement<Props> => {
  return isValidElement(child) && child.type === component
}

const toLeafSpec = (
  element: React.ReactElement<TopNavigationLinkProps | TopNavigationFooterLinkProps>,
  fallbackKey: string,
  currentPath?: string,
): NavigationLeafSpec => ({
  isActive:
    element.props.isActive ?? isCurrentPathMatch(currentPath, element.props.to),
  key: getElementKey(element, fallbackKey),
  label: element.props.children,
  rel: element.props.rel,
  target: element.props.target,
  to: element.props.to,
})

const parseSection = (
  element: React.ReactElement<TopNavigationSectionProps>,
  sectionIndex: number,
  currentPath?: string,
): NavigationSectionSpec | null => {
  const items: NavigationLeafSpec[] = []

  for (const [itemIndex, child] of Children.toArray(
    element.props.children,
  ).entries()) {
    if (!isElementOfType(child, TopNavigationLinkMarker)) {
      continue
    }

    items.push(
      toLeafSpec(
        child,
        `section-${sectionIndex}-item-${itemIndex}`,
        currentPath,
      ),
    )
  }

  if (items.length === 0) {
    return null
  }

  return {
    items,
    key: getElementKey(element, `section-${sectionIndex}`),
    label: element.props.label,
  }
}

const parseMenu = (
  element: React.ReactElement<TopNavigationMenuProps>,
  menuIndex: number,
  currentPath?: string,
): NavigationMenuSpec | null => {
  const disabled = element.props.disabled ?? false
  let footerLink: NavigationLeafSpec | null = null
  const sections: NavigationSectionSpec[] = []

  for (const [childIndex, child] of Children.toArray(
    element.props.children,
  ).entries()) {
    if (isElementOfType(child, TopNavigationSectionMarker)) {
      const section = parseSection(child, childIndex, currentPath)
      if (section) {
        sections.push(section)
      }
      continue
    }

    if (isElementOfType(child, TopNavigationFooterLinkMarker)) {
      footerLink = toLeafSpec(child, `menu-${menuIndex}-footer`, currentPath)
    }
  }

  // A disabled menu still renders (greyed out, with its tooltip) regardless
  // of whether it currently has any sections/footer link — it's never opened,
  // so there's nothing that needs real content.
  if (!disabled && sections.length === 0 && footerLink === null) {
    return null
  }

  const footerIsActive = footerLink !== null ? footerLink.isActive : false

  return {
    disabled,
    disabledTooltip: element.props.disabledTooltip ?? null,
    footerLink,
    isActive:
      !disabled &&
      (sections.some(section => section.items.some(item => item.isActive)) ||
        footerIsActive),
    key: getElementKey(element, `menu-${menuIndex}`),
    label: element.props.label,
    sections,
  }
}

export const parseTopNavigationChildren = (
  children: React.ReactNode,
  currentPath?: string,
): ParsedTopNavigationChildren => {
  let brand: NavigationBrandSpec | null = null
  const items: NavigationItemSpec[] = []

  for (const [childIndex, child] of Children.toArray(children).entries()) {
    if (isElementOfType(child, TopNavigationBrandMarker)) {
      brand = {
        key: getElementKey(child, 'brand'),
        label: child.props.children,
        to: child.props.to,
      }
      continue
    }

    if (isElementOfType(child, TopNavigationLinkMarker)) {
      items.push({
        kind: 'link',
        link: toLeafSpec(child, `top-link-${childIndex}`, currentPath),
      })
      continue
    }

    if (isElementOfType(child, TopNavigationMenuMarker)) {
      const menu = parseMenu(child, childIndex, currentPath)
      if (menu) {
        items.push({ kind: 'menu', menu })
      }
    }
  }

  return { brand, items }
}

export const getDefaultSectionKey = (
  menu: NavigationMenuSpec,
): string | null => {
  const activeSection = menu.sections.find(section =>
    section.items.some(item => item.isActive),
  )

  return activeSection?.key ?? menu.sections[0]?.key ?? null
}

export const resolveOpenMenuSection = (
  menu: NavigationMenuSpec | null,
  activeSectionKey: string | null,
): NavigationSectionSpec | null => {
  if (menu === null) {
    return null
  }

  if (activeSectionKey) {
    const activeSection = menu.sections.find(
      section => section.key === activeSectionKey,
    )
    if (activeSection) {
      return activeSection
    }
  }

  return menu.sections[0] ?? null
}

export const DownChevron = ({ sx }: ChevronProps): React.ReactElement => (
  <Box
    aria-hidden
    sx={[
      (theme: Theme) => ({
        alignItems: 'center',
        color: getMutedColor(theme),
        display: 'inline-flex',
        height: 14,
        justifyContent: 'center',
        width: 14,
      }),
      ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
    ]}
  >
    <ExpandMoreIcon sx={{ fontSize: 14 }} />
  </Box>
)

export const RightChevron = ({ sx }: ChevronProps): React.ReactElement => (
  <Box
    aria-hidden
    sx={[
      (theme: Theme) => ({
        alignItems: 'center',
        color: theme.palette.primary.main,
        display: 'inline-flex',
        height: 16,
        justifyContent: 'center',
        width: 16,
      }),
      ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
    ]}
  >
    <ChevronRightIcon sx={{ fontSize: 16 }} />
  </Box>
)

export const GearIcon = (): React.ReactElement => (
  <Box
    aria-hidden
    sx={theme => ({
      alignItems: 'center',
      color: theme.palette.primary.main,
      display: 'inline-flex',
      height: 16,
      justifyContent: 'center',
      width: 16,
    })}
  >
    <SettingsIcon sx={{ fontSize: 16 }} />
  </Box>
)

const getLinkColor = (theme: Theme, isActive: boolean) =>
  isActive
    ? (theme.palette.primary.dark ?? theme.palette.primary.main)
    : theme.palette.text.primary

const getMutedColor = (theme: Theme) => theme.palette.text.primary

export const NavigationLink = ({
  children,
  isActive = false,
  linkComponent,
  onClick,
  rel,
  sx,
  target,
  to,
}: NavigationLinkRendererProps): React.ReactElement => {
  const linkSx = (theme: Theme): SxProps<Theme> => ({
    color: getLinkColor(theme, isActive),
    textDecoration: 'none',
  })

  if (linkComponent) {
    return (
      <Box
        component={linkComponent}
        onClick={onClick}
        rel={rel}
        sx={theme => ({
          ...linkSx(theme),
          ...(typeof sx === 'function' ? sx(theme) : sx),
        })}
        target={target}
        to={to}
      >
        {children}
      </Box>
    )
  }

  return (
    <Box
      component="a"
      href={to}
      onClick={onClick}
      rel={rel}
      sx={theme => ({
        ...linkSx(theme),
        ...(typeof sx === 'function' ? sx(theme) : sx),
      })}
      target={target}
    >
      {children}
    </Box>
  )
}

export const topNavigationStyles = {
  borderColor: PaletteGray.p400,
  brandTextColor: '#F0F5FF',
  dropdownShadow:
    '0px 20px 24px 0px rgba(10, 13, 18, 0.08), 0px 8px 8px 0px rgba(10, 13, 18, 0.03), 0px 3px 3px 0px rgba(10, 13, 18, 0.04)',
  getMutedColor,
  shellShadow:
    '0px 8px 8px 0px rgba(33, 62, 109, 0.03), 0px 3px 3px 0px rgba(5, 30, 71, 0.04)',
  utilityActionColor: PaletteGray.p700,
}

TopNavigationBrandMarker.displayName = 'TopNavigation.Brand'
TopNavigationLinkMarker.displayName = 'TopNavigation.Link'
TopNavigationMenuMarker.displayName = 'TopNavigation.Menu'
TopNavigationSectionMarker.displayName = 'TopNavigation.Section'
TopNavigationFooterLinkMarker.displayName = 'TopNavigation.FooterLink'
